function normalizeSpaces(input) {
  return String(input || '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();
}

function splitIntoSentences(text) {
  const s = normalizeSpaces(text);
  if (!s) return [];
  // Very small heuristic sentence splitter; good enough for AI-authored news blurbs.
  const parts = s.match(/[^.!?]+[.!?]+|[^.!?]+$/g);
  return (parts || []).map((p) => p.trim()).filter(Boolean);
}

function looksLikeHtml(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || '').trim());
}

function looksStructuredMarkdown(value) {
  const s = String(value || '');
  return /\n{2,}/.test(s) || /^#{1,6}\s+/m.test(s) || /^\s*[-*+]\s+/m.test(s);
}

function injectHeadingsFromInlineTitleCase(text) {
  const s = normalizeSpaces(text);
  if (!s) return '';

  // Prefer explicit cue phrases first (high precision).
  const cue =
    /(Focus on|Integration of|Market Presence|Outlook|Background|Key takeaways|What happened|Why it matters|Regulatory context)\b/;
  let out = s.replace(
    new RegExp(`([.!?])\\s+(${cue.source}[^.!?]{0,80})\\s+(?=[A-Z])`, 'g'),
    (_m, punc, heading) => `${punc}\n\n## ${heading.trim()}\n\n`
  );

  // Then a generic Title-Case heading detector between sentences.
  out = out.replace(
    /([.!?])\s+([A-Z][A-Za-z0-9’'"&()-]+(?:\s+(?:[A-Z][A-Za-z0-9’'"&()-]+|and|of|to|in|on|for|with|&)){2,10})\s+(?=[A-Z])/g,
    (m, punc, heading) => {
      const h = String(heading || '').trim();
      const wordCount = h.split(/\s+/).filter(Boolean).length;
      if (h.length < 12 || h.length > 80) return m;
      if (wordCount < 3) return m;
      // Avoid turning names/quotes into headings.
      if (/[,:;]/.test(h)) return m;
      return `${punc}\n\n## ${h}\n\n`;
    }
  );

  return out;
}

export function splitOneLineIntoTitleDekBody(oneLine) {
  const raw = normalizeSpaces(oneLine);
  if (!raw) return { title: '', dek: '', body: '' };

  const idx = raw.search(/[.!?]\s/);
  if (idx === -1) return { title: '', dek: '', body: raw };

  const titleCandidate = raw.slice(0, idx + 1).replace(/[.!?]$/, '').trim();
  const rest = raw.slice(idx + 1).trim();

  const titleOk =
    titleCandidate.length >= 18 &&
    titleCandidate.length <= 120 &&
    titleCandidate.split(/\s+/).filter(Boolean).length <= 14;

  if (!titleOk) return { title: '', dek: '', body: raw };

  const sentences = splitIntoSentences(rest);
  const dek = sentences.slice(0, 2).join(' ').trim();
  const body = sentences.slice(2).join(' ').trim() || rest;

  return { title: titleCandidate, dek, body };
}

export function autoFormatMarkdownFromPlainText(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  if (looksLikeHtml(raw) || looksStructuredMarkdown(raw)) return raw;

  let s = injectHeadingsFromInlineTitleCase(raw);

  // If we still have no real paragraph breaks, chunk into readable paragraphs.
  if (!/\n/.test(s)) {
    const sentences = splitIntoSentences(s);
    if (!sentences.length) return '';

    const paragraphs = [];
    for (let i = 0; i < sentences.length; ) {
      const take = i < 4 ? 2 : 3; // tighter intro, slightly longer later
      paragraphs.push(sentences.slice(i, i + take).join(' '));
      i += take;
    }
    s = paragraphs.join('\n\n');
  } else {
    // Clean up excessive whitespace around headings.
    s = s.replace(/\n{3,}/g, '\n\n').trim();
  }

  return s;
}

export function shouldAutoFormatMarkdown(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  if (looksLikeHtml(s) || looksStructuredMarkdown(s)) return false;
  // Only auto-format when it looks like an article blob.
  return s.length >= 240 && !s.includes('\n');
}


